// ============================================================================
// FNE-BRIDGE Frontend - Composants de chargement (Spinner + Skeleton)
// ============================================================================

import React from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';

// --- Spinner plein écran ---
export interface SpinnerProps {
  message?: string;
  size?: number;
  fullScreen?: boolean;
}

export const Spinner: React.FC<SpinnerProps> = ({
  message,
  size = 40,
  fullScreen = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        ...(fullScreen
          ? { position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(255,255,255,0.9)' }
          : { py: 6 }),
      }}
    >
      <CircularProgress size={size} color="primary" />
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Box>
  );
};

// --- Page skeleton ---
export const PageSkeleton: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="text" width={240} height={32} sx={{ mb: 2 }} />
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} variant="rounded" width="25%" height={120} sx={{ borderRadius: 2 }} />
      ))}
    </Box>
    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2 }} />
  </Box>
);

// --- Card skeleton ---
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ flex: '1 1 200px' }}>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      </Box>
    ))}
  </Box>
);

// --- Table skeleton ---
export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({
  rows = 5,
  cols = 5,
}) => (
  <Box>
    <Box sx={{ display: 'flex', gap: 2, mb: 1, px: 2, py: 1.5 }}>
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} variant="text" width={`${100 / cols}%`} height={20} />
      ))}
    </Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 2, px: 2, py: 1 }}>
        {Array.from({ length: cols }).map((_, j) => (
          <Skeleton key={j} variant="text" width={`${100 / cols}%`} height={18} />
        ))}
      </Box>
    ))}
  </Box>
);
